import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  ArrowLeft, 
  User, 
  MessageCircle, 
  ThumbsUp, 
  Trophy,
  Calendar,
  BarChart3
} from "lucide-react";

// Mock user data
const mockUser = {
  id: 1,
  username: "토론러123",
  email: "debater123@example.com",
  avatar: "",
  joinDate: "2023-12-01",
  totalDebates: 15,
  totalVotes: 1247,
  totalComments: 89,
  winRate: 68,
  favoriteCategory: "사회"
};

// Mock user's debates
const mockUserDebates = [
  {
    id: 1,
    title: "재택근무가 생산성에 도움이 될까?",
    category: "사회",
    status: "진행중",
    myVote: "agree",
    totalVotes: 2103,
    createdAt: "2024-01-01"
  },
  {
    id: 2,
    title: "AI가 인간의 일자리를 대체할까?",
    category: "테크",
    status: "완료",
    myVote: "disagree",
    totalVotes: 1856,
    createdAt: "2023-12-28"
  }
];

// Mock user's comments
const mockUserComments = [
  {
    id: 1,
    debateTitle: "재택근무가 생산성에 도움이 될까?",
    content: "재택근무 하면서 통근 시간이 없어져서 오히려 더 많은 일을 할 수 있게 되었어요.",
    likes: 23,
    side: "agree",
    createdAt: "2시간 전"
  },
  {
    id: 2,
    debateTitle: "AI가 인간의 일자리를 대체할까?",
    content: "AI는 도구일 뿐이고, 인간의 창의성과 감정은 대체할 수 없다고 생각합니다.",
    likes: 15,
    side: "disagree",
    createdAt: "1일 전"
  }
];

const Profile = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" asChild>
                <Link to="/" className="flex items-center space-x-2">
                  <ArrowLeft className="w-4 h-4" />
                  <span>돌아가기</span>
                </Link>
              </Button>
              
              <div className="h-6 w-px bg-gray-300" />
              
              <Link to="/" className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-orange-500 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">B</span>
                </div>
                <span className="text-xl font-bold gradient-text">BothSides</span>
              </Link>
            </div>
            
            <div className="flex items-center space-x-4">
              <Button variant="outline">
                설정
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Profile Header */}
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="flex items-start space-x-6">
              <Avatar className="w-20 h-20">
                <AvatarImage src={mockUser.avatar} />
                <AvatarFallback className="text-2xl">{mockUser.username[0]}</AvatarFallback>
              </Avatar>
              
              <div className="flex-1">
                <h1 className="text-2xl font-bold mb-2">{mockUser.username}</h1>
                <p className="text-gray-600 mb-4">{mockUser.email}</p>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{mockUser.totalDebates}</div>
                    <div className="text-sm text-gray-600">참여 토론</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{mockUser.totalVotes}</div>
                    <div className="text-sm text-gray-600">총 투표</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">{mockUser.totalComments}</div>
                    <div className="text-sm text-gray-600">작성 댓글</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">{mockUser.winRate}%</div>
                    <div className="text-sm text-gray-600">승률</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-6 pt-6 border-t">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-600">
                      {new Date(mockUser.joinDate).toLocaleDateString('ko-KR')} 가입
                    </span>
                  </div>
                  <Badge variant="secondary">
                    선호 카테고리: {mockUser.favoriteCategory}
                  </Badge>
                </div>
                
                <Button className="bg-gradient-to-r from-blue-500 to-orange-500 hover:from-blue-600 hover:to-orange-600">
                  프로필 수정
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Activity Tabs */}
        <Tabs defaultValue="debates" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="debates" className="flex items-center space-x-2">
              <BarChart3 className="w-4 h-4" />
              <span>참여 토론</span>
            </TabsTrigger>
            <TabsTrigger value="comments" className="flex items-center space-x-2">
              <MessageCircle className="w-4 h-4" />
              <span>작성 댓글</span>
            </TabsTrigger>
            <TabsTrigger value="achievements" className="flex items-center space-x-2">
              <Trophy className="w-4 h-4" />
              <span>업적</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="debates" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">참여한 토론</h2>
              <span className="text-sm text-gray-600">{mockUserDebates.length}개</span>
            </div>
            
            <div className="space-y-4">
              {mockUserDebates.map((debate) => (
                <Card key={debate.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <Badge variant="secondary">{debate.category}</Badge>
                          <Badge variant={debate.status === "진행중" ? "default" : "outline"}>
                            {debate.status}
                          </Badge>
                          <Badge variant={debate.myVote === "agree" ? "default" : "destructive"} 
                                 className={debate.myVote === "agree" ? "bg-green-500" : "bg-red-500"}>
                            {debate.myVote === "agree" ? "찬성" : "반대"}
                          </Badge>
                        </div>
                        
                        <h3 className="font-semibold mb-2 hover:text-blue-600 cursor-pointer">
                          <Link to={`/debate/${debate.id}`}>
                            {debate.title}
                          </Link>
                        </h3>
                        
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <span>{debate.totalVotes.toLocaleString()}명 참여</span>
                          <span>{debate.createdAt}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="comments" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">작성한 댓글</h2>
              <span className="text-sm text-gray-600">{mockUserComments.length}개</span>
            </div>
            
            <div className="space-y-4">
              {mockUserComments.map((comment) => (
                <Card key={comment.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start space-x-3">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-sm font-medium text-gray-600">
                            {comment.debateTitle}
                          </span>
                          <Badge variant={comment.side === "agree" ? "default" : "destructive"} 
                                 className={comment.side === "agree" ? "bg-green-500" : "bg-red-500"}>
                            {comment.side === "agree" ? "찬성" : "반대"}
                          </Badge>
                        </div>
                        
                        <p className="mb-3">{comment.content}</p>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2 text-sm text-gray-600">
                            <ThumbsUp className="w-4 h-4" />
                            <span>{comment.likes}</span>
                            <span>•</span>
                            <span>{comment.createdAt}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="achievements" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">업적</h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Card className="text-center p-6">
                <Trophy className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                <h3 className="font-semibold mb-2">첫 토론 참여</h3>
                <p className="text-sm text-gray-600">첫 번째 토론에 참여했습니다</p>
              </Card>
              
              <Card className="text-center p-6">
                <MessageCircle className="w-12 h-12 text-blue-500 mx-auto mb-4" />
                <h3 className="font-semibold mb-2">활발한 토론가</h3>
                <p className="text-sm text-gray-600">50개 이상의 댓글을 작성했습니다</p>
              </Card>
              
              <Card className="text-center p-6 opacity-50">
                <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="font-semibold mb-2 text-gray-500">인기 토론가</h3>
                <p className="text-sm text-gray-400">100개 이상의 좋아요를 받으세요</p>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default Profile;